import {useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useRouteMatch, useHistory, useParams, Redirect, Route, Switch} from 'react-router-dom';
import {OfficeContext} from '../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {PlaylistsRepository} from '../../../../../../repositories/playlists';
import {translations} from '../../../../../../i18n/translations';
import PlaylistItemUsersProfessionalCaregiverOfficeScreen from './users/playlist-item-users.professional-caregiver.screen';
import PlaylistItemDetailsProfessionalCaregiverOfficeScreen from './details/playlist-item-details.professional-caregiver.screen';

enum PathExtensions {
  Details = 'details',
  Users = 'users',
}

export default function PlaylistItemProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  // Get params
  // - id: playlist id
  // - entityId: entity id
  // - itemId: playlist item id
  let {playlistId, entityId, itemId}: {playlistId?: string; entityId?: string; itemId?: string} =
    useParams();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Validate and get the playlist item
  useEffect(() => {
    const getPlaylistItem = async () => {
      try {
        // Check ids
        if (!playlistId || !entityId || !itemId) {
          // Error
          throw new Error('Empty entity, playlist, or playlist item id.');
        }

        // Get from backend
        // Note: Don't localize because we need the original model for the playlist item form
        const result =
          await PlaylistsRepository.instance.getPlaylistItemByIdForEntityAsAffiliatedForEntity(
            entityId,
            playlistId,
            itemId,
            false
          );

        if (result) {
          // Set playlist item in context
          officeContext.setPlaylistItemForm(result);
        } else {
          // Error
          throw new Error('Empty playlist item.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the playlist item.', error);

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.playlists.professional_caregiver.playlist_item.errors
              .item_does_not_exist_error
          ),
        });

        // Redirect
        history.push(`${url.replace(`/items/${itemId}`, '/items')}`);
      }
    };

    // Get playlist item
    getPlaylistItem();

    // Clean up
    return () => {
      // Clear playlist item
      officeContext.clearPlaylistItemForm();

      // Log
      console.debug('🧹 Playlist item form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  return (
    <>
      {/* Main content */}
      <Switch>
        {/* Playlist item details */}
        <Route exact path={`${path}/${PathExtensions.Details}`}>
          <PlaylistItemDetailsProfessionalCaregiverOfficeScreen canWrite={false} />
        </Route>
        {/* Playlist item users */}
        <Route exact path={`${path}/${PathExtensions.Users}`}>
          <PlaylistItemUsersProfessionalCaregiverOfficeScreen canWrite={canWrite} />
        </Route>
        {/** Default route */}
        <Route path={path}>
          <Redirect to={`${url}/${PathExtensions.Users}`} />
        </Route>
      </Switch>
    </>
  );
}
